import { defineStore } from 'pinia'
import { ref } from 'vue'
import axios from 'axios'
import { resolveApiBaseUrl } from '@/api/apiClient'

const RETRY_INTERVAL_MS = 5000
const HEALTH_CHECK_TIMEOUT_MS = 5000

/**
 * Глобальный статус доступности бэкенда.
 * Когда бэкенд недоступен (не поднялся / перезапускается), приложение
 * показывает полноэкранную заглушку и периодически проверяет /liveness.
 */
export const useAppStatusStore = defineStore('appStatus', () => {
  const isBackendUnavailable = ref(false)
  const isChecking = ref(false)
  let retryTimer: ReturnType<typeof setTimeout> | null = null

  const scheduleRetry = () => {
    if (retryTimer) clearTimeout(retryTimer)
    retryTimer = setTimeout(() => { checkBackend() }, RETRY_INTERVAL_MS)
  }

  const checkBackend = async (): Promise<boolean> => {
    if (isChecking.value) return false
    isChecking.value = true
    try {
      // Отдельный axios без интерсепторов, чтобы проверка не зациклила заглушку
      await axios.get(`${resolveApiBaseUrl()}/liveness`, { timeout: HEALTH_CHECK_TIMEOUT_MS })
      if (retryTimer) {
        clearTimeout(retryTimer)
        retryTimer = null
      }
      if (isBackendUnavailable.value) {
        // Перезагружаем страницу, чтобы приложение стартовало с чистым состоянием
        window.location.reload()
      }
      return true
    } catch {
      scheduleRetry()
      return false
    } finally {
      isChecking.value = false
    }
  }

  const markBackendUnavailable = () => {
    if (isBackendUnavailable.value) return
    isBackendUnavailable.value = true
    scheduleRetry()
  }

  return {
    isBackendUnavailable,
    isChecking,
    markBackendUnavailable,
    checkBackend,
  }
})
